import { Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation } from "@angular/core";
import { MediaObserver } from "@angular/flex-layout";

import * as _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService, UserAndToken } from "@service/authentication.service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";

import { Router } from "@angular/router";
import { NotificationService } from "@service/notification.service";
import { WebSocketService } from "@service/websocket.service";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: UserAndToken;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  public notificationCount: number = 0;
  public ringBell = false;

  @HostBinding("class.fixed-top")
  public isFixed = false;

  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private _notificationService: NotificationService,
    private _websocketService: WebSocketService
  ) {
    this._authenticationService.currentUser.subscribe(x => (this.currentUser = x));

    this.languageOptions = {
      en: {
        title: "English",
        flag: "us"
      },
      fr: {
        title: "French",
        flag: "fr"
      },
      de: {
        title: "German",
        flag: "de"
      },
      pt: {
        title: "Portuguese",
        flag: "pt"
      }
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem("prevSkin");

    if (this.currentSkin === "dark") {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : "default" } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem("prevSkin", this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: "dark" } }, { emitEvent: true });
    }
  }

  toggleNotificationPanel() {
    this._notificationService.togglePanel();
  }

  /**
   * Logout method
   */
  logout() {
    this._websocketService.disconnect();
    this._notificationService.updateTitle(0);
    this._authenticationService.logout();
    this._router.navigate(["/inicio"]);
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === "horizontal";
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === "vertical") {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === "fixed-top") {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == "horizontal") {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive("bs-gt-xl");
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang
    });

    this.initializeTokenAndNotifications();
  }

  private initializeTokenAndNotifications(): void {
    // Verificar el token
    this._authenticationService.verifyToken()?.subscribe(resp => {
      if (resp?.ok) {
        // Suscribirse al usuario actual
        this._authenticationService.currentUser.pipe(takeUntil(this._unsubscribeAll)).subscribe(user => {
          this.currentUser = user;

          // Suscribirse a las notificaciones
          this._notificationService.notificationCount
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
              if (this.notificationCount < count) {
                this.ringBell = true;
                setTimeout(() => (this.ringBell = false), 4000);
              }
              this.notificationCount = count;
            });
        });
      } else {
        setTimeout(() => this.initializeTokenAndNotifications(), 2000);
      }
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  get userName(): string {
    const name = this.currentUser.user.name?.split(" ")?.[0] || "";
    const lastName = this.currentUser.user.last_name || "";
    return name + " " + lastName;
  }

  get userDomain(): string {
    const email = this.currentUser.user.email;
    return email;
    //return email.substring(email.indexOf('@') + 1);
  }

  get userInitials(): string {
    const name = this.currentUser.user.name || "";
    const lastName = this.currentUser.user.last_name || "";
    return name.charAt(0) + lastName.charAt(0);
  }
}
